
import { Form, Input, Button, Select, message } from 'antd'
import { ArrowLeftOutlined, DownOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { resourceApi, CreateResourceRequest } from '../services/resources'
import { categoryApi } from '../services/categories'

const { Option } = Select

const AddResource = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()

  // Fetch categories for the dropdown
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: categoryApi.getCategories,
    select: (data) => data.success ? data.data : []
  })

  // Create resource mutation
  const createMutation = useMutation({
    mutationFn: (resourceData: CreateResourceRequest) => resourceApi.createResource(resourceData),
    onSuccess: () => {
      message.success('Resource created successfully!')
      navigate('/resources')
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create resource')
    },
  })

  const onFinish = (values: any) => {
    const resourceData: CreateResourceRequest = {
      title: values.title,
      description: values.description || '',
      resourceType: values.resourceType,
      link: values.link,
      categoryId: values.categoryId
    }
    
    createMutation.mutate(resourceData)
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Back Button */}
      <div className="mb-6">
        <Button 
          type="text" 
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          className="text-gray-600 hover:text-gray-900 p-2 h-auto font-medium"
        >
          Back
        </Button>
      </div>

      {/* Form Card */}
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-8">Add Resources</h1>
          
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            className=""
          >
            {/* Title */}
            <Form.Item
              label={<span className="text-sm font-medium text-gray-700">Title*</span>}
              name="title"
              rules={[{ required: true, message: 'Please enter title' }]}
              required={false}
            >
              <Input 
                placeholder="Enter title"
                className="h-9 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
              />
            </Form.Item>

            {/* Description */}
            <Form.Item
              label={<span className="text-sm font-medium text-gray-700">Description</span>}
              name="description"
            >
              <Input.TextArea 
                placeholder="Enter description"
                rows={3}
                className="rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
              />
            </Form.Item>

            {/* Category */}
            <Form.Item
              label={<span className="text-sm font-medium text-gray-700">Category*</span>}
              name="categoryId"
              rules={[{ required: true, message: 'Please select a category' }]}
              required={false}
            >
              <Select
                placeholder="Select a category"
                className="h-9 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
                suffixIcon={<DownOutlined className="text-gray-400" />}
                loading={categoriesLoading}
              >
                {(categoriesData || []).map(category => (
                  <Option key={category._id} value={category._id}>
                    {category.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {/* Resource Type */}
            <Form.Item
              label={<span className="text-sm font-medium text-gray-700">Resource Type*</span>}
              name="resourceType"
              rules={[{ required: true, message: 'Please select a resource type' }]}
              required={false}
            >
              <Select
                placeholder="Select a Resource Type"
                className="h-9 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
                suffixIcon={<DownOutlined className="text-gray-400" />}
              >
                <Option value="guide">Guide</Option>
                <Option value="tutorial">Tutorial</Option>
                <Option value="documentation">Documentation</Option>
                <Option value="video">Video</Option>
                <Option value="article">Article</Option>
              </Select>
            </Form.Item>

            {/* Link */}
            <Form.Item
              label={<span className="text-sm font-medium text-gray-700">Link*</span>}
              name="link"
              rules={[
                { required: true, message: 'Please enter link' },
                { type: 'url', message: 'Please enter a valid URL' }
              ]}
              required={false}
            >
              <Input 
                placeholder="Add link"
                className="h-9 rounded-lg border-gray-300 focus:border-primary focus:ring-primary"
              />
            </Form.Item>

            {/* Submit Button */}
            <Form.Item className="mb-0">
              <Button 
                type="primary" 
                htmlType="submit"
                loading={createMutation.isPending}
                className="w-full h-12 bg-primary hover:bg-primary/90 border-primary hover:border-primary/90 rounded-lg font-medium text-base"
              >
                Add Resources
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default AddResource
